import React, { createContext, useContext, useState, useEffect } from "react";
import {
  LINE_PURPOSE,
  LINE_TYPE,
  USER_OPERATIONS,
  APPLICATION_VARIANTS,
} from "../../constants";
import {
  buildErrorMessage,
  fetchLinesForGivenPlant,
  fetchLinesForGivenPlantAndPurpose,
  fetchPlants,
} from "../../apis/calls";
import config from "../../config/config";

import { useAuthorizationContext } from "../../components/AuthorizationProvider/AuthorizationProvider";

export const PlantAndLinesStoreContext = createContext(null);

const getFromLocalStorageCurry = (keyStr) => () => {
  const value = localStorage.getItem(`${keyStr}Value`);
  const label = localStorage.getItem(`${keyStr}Label`);

  if (value && label) return { value, label };
  return null;
};

const saveToLocalStorageCurry = (keyStr) => (data) => {
  localStorage.setItem(`${keyStr}Value`, data.value);
  localStorage.setItem(`${keyStr}Label`, data.label);
};

// const deleteFromLocalStorage = localStorage.clear();

const getPlantFromLocalStorage = getFromLocalStorageCurry("plant");
const getLineFromLocalStorage = getFromLocalStorageCurry("line");

const savePlantToLocalStorage = saveToLocalStorageCurry("plant");
const saveLineToLocalStorage = saveToLocalStorageCurry("line");

const initialPlant = { value: null, label: null };
const initialLine = { value: null, label: "" };

//NOTE: tests assume that pune plant and d3 line will be the defaults. - for IPMS4
export default ({
  children,
  defaultPlant = initialPlant,
  defaultLine = initialLine,
  defaultAggregateLine = initialLine,
}) => {
  const { isAuthenticated, authPolicies } = useAuthorizationContext();
  const [plant, setPlant] = useState(defaultPlant);
  const [plantOptions, setPlantOptions] = useState([]);
  const [line, setLine] = useState(defaultLine);
  const [lineOptions, setLineOptions] = useState([]);
  const [aggregateLine, setAggregateLine] = useState(defaultAggregateLine);
  const [aggregateLineOptions, setAggregateLineOptions] = useState([]);
  const [
    aggregatesWithAccessToDispatch,
    setAggregatesWithAccessToDispatch,
  ] = useState([]);

  const loadChassisLinesForPlant = (plantValue) =>
    fetchLinesForGivenPlantAndPurpose(plantValue, LINE_PURPOSE.CHASSIS)
      .then((res) => res.data)
      .then((data) => {
        let chassisLinesInAPlant = data
          .filter((line) => line.lineType === LINE_TYPE.MAIN)
          .map((record) => {
            return { value: record.line, label: record.lineName };
          });
        setLineOptions(chassisLinesInAPlant);

        const savedLineOption =
          getPlantFromLocalStorage()?.value === plantValue &&
          getLineFromLocalStorage();

        const defaultLineOption = savedLineOption ||
          chassisLinesInAPlant[0] || {
            value: null,
            label: "",
          };
        setLine(defaultLineOption);
      })
      .catch((error) => {
        console.error(buildErrorMessage(error));
      });

  const loadAggregatesForWhichNewDispatchIsEnabled = async () => {
    const { data: allLines } = await fetchLinesForGivenPlant(plant.value);

    const allowedResourcesForUser = authPolicies?.find(
      (record) => record?.operation === USER_OPERATIONS.AGGREGATE_DISPATCH
    );

    const arrayOfAllowedResources = allowedResourcesForUser?.allowed.map(
      (record) => record.split(":")
    );
    const linesForGivenUser = arrayOfAllowedResources?.map(
      (record) => record[record?.length - 1]
    );

    const aggregatesWithDispatch = linesForGivenUser
      ?.map((line) => {
        const lineIndex = allLines.findIndex((item) => item.line === line);
        if (lineIndex !== -1) return allLines[lineIndex].shopType;
        return null;
      })
      .filter((item) => item);

    setAggregatesWithAccessToDispatch([...new Set(aggregatesWithDispatch)]);
  };

  useEffect(() => {
    // Note: Here saving of plant to storage has to happen after loading Lines
    // so that when loading default line we can do a check if the line in storage
    // is of previous plant - from storage, before plant is updated in storage
    if (isAuthenticated && plant.value) {
      loadChassisLinesForPlant(plant.value).then(() => {
        if (plant?.value && plant?.label) savePlantToLocalStorage(plant);
      });
      loadAggregatesForWhichNewDispatchIsEnabled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, plant, authPolicies.length]);

  useEffect(() => {
    if (line?.value && line?.label) saveLineToLocalStorage(line);
  }, [line]);

  useEffect(() => {
    isAuthenticated &&
      fetchPlants()
        .then((res) => res.data)
        .then((data) => {
          const plantOptions = data.map((record) => {
            return { value: record.plant, label: record.plantName };
          });
          setPlantOptions(plantOptions);
          const defaultPlantOption =
            APPLICATION_VARIANTS.MES4 === config.APPLICATION_VARIANT
              ? plantOptions[0]
              : getPlantFromLocalStorage() || plantOptions[0];
          setPlant(defaultPlantOption);
          return defaultPlantOption;
        })
        .catch((error) => {
          console.error(buildErrorMessage(error));
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <>
      <PlantAndLinesStoreContext.Provider
        value={{
          plantState: [plant, (value) => setPlant(value)],
          plantStateUserChange: [plantOptions, (value) => setPlant(value)],
          plantOptions: [plantOptions, null],
          lineState: [line, (value) => setLine(value)],
          lineOptions: [lineOptions, null],
          aggregateLineState: [
            aggregateLine,
            (value) => setAggregateLine(value),
          ],
          aggregateLineOptions: [
            aggregateLineOptions,
            (value) => setAggregateLineOptions(value),
          ],
          aggregatesWithAccessToDispatch,
        }}
      >
        {children}
      </PlantAndLinesStoreContext.Provider>
    </>
  );
};
export const usePlantAndLinesStore = () =>
  useContext(PlantAndLinesStoreContext);
